<template>
  <div
    class="im-cascader"
    :class="{ 'is-opened': visible, 'is-disabled': disabled }"
  >
    <div class="im-cascader__trigger" ref="trigger" @click="togglePanel">
      <span class="im-cascader__label" v-if="hasValue">
        {{ displayLabel }}
      </span>
      <span class="im-cascader__placeholder" v-else>{{ placeholder }}</span>
      <div class="im-cascader__suffix">
        <i class="im-cascader__clear" v-if="clearable && hasValue" @click.stop="clearValue">×</i>
        <i class="im-cascader__arrow" :class="{ 'is-reverse': visible, 'has-clear': clearable && hasValue }" />
      </div>
    </div>
    <transition name="im-cascader-fade">
      <div
        v-show="visible"
        ref="panel"
        class="im-cascader__panel"
        :style="panelStyle"
      >
        <div class="im-cascader__menu" v-for="(col, colIdx) in menuColumns" :key="colIdx">
          <div class="im-cascader__menu-wrap">
            <div
              v-for="opt in col"
              :key="opt[valueKey]"
              class="im-cascader__node"
              :class="nodeClass(opt, colIdx)"
              @click="handleNodeClick(opt, colIdx)"
            >
              <span
                class="im-cascader__radio"
                :class="{ 'is-checked': isChecked(opt, colIdx) }"
              >
                <span class="im-cascader__radio-inner" />
              </span>
              <span class="im-cascader__node-label">{{ opt[labelKey] }}</span>
              <i v-if="hasChildren(opt)" class="im-cascader__node-arrow">›</i>
            </div>
            <div class="im-cascader__empty" v-if="!col.length">暂无数据</div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { insertStep } from '~/utils/event'

export default {
  name: 'ImCascader',
  props: {
    value: {
      type: [String, Number, Array],
      default: () => []
    },
    options: {
      type: Array,
      default: () => []
    },
    clearable: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    cascaderProps: {
      type: Object,
      default: () => ({})
    },
    targetKey: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      visible: false,
      expandingPath: [],
      placement: 'bottom',
      panelStyle: {}
    }
  },
  computed: {
    mergedProps() {
      return {
        emitPath: true,
        value: 'value',
        label: 'label',
        children: 'children',
        ...this.cascaderProps
      }
    },
    valueKey() { return this.mergedProps.value },
    labelKey() { return this.mergedProps.label },
    childrenKey() { return this.mergedProps.children },

    hasValue() {
      return Array.isArray(this.value) && this.value.length > 0
    },

    menuColumns() {
      const columns = [this.options]
      let current = this.options
      for (const node of this.expandingPath) {
        current = node[this.childrenKey] || []
        columns.push(current)
      }
      return columns
    },

    displayLabel() {
      if (!this.hasValue) return ''
      return this.getLabelPath(this.value)
    }
  },
  watch: {
    value: {
      handler() {
        if (this.visible) this.syncExpandingPath()
      },
      deep: true
    },
    menuColumns() {
      if (this.visible) {
        this.$nextTick(() => this.updatePlacement())
      }
    }
  },
  mounted() {
    if (this.$refs.panel) {
      document.body.appendChild(this.$refs.panel)
    }
    document.addEventListener('mousedown', this.onDocumentClick, true)
    window.addEventListener('resize', this.onWindowChange)
    window.addEventListener('scroll', this.onWindowChange, true)
  },
  beforeDestroy() {
    if (this.$refs.panel && this.$refs.panel.parentNode === document.body) {
      document.body.removeChild(this.$refs.panel)
    }
    document.removeEventListener('mousedown', this.onDocumentClick, true)
    window.removeEventListener('resize', this.onWindowChange)
    window.removeEventListener('scroll', this.onWindowChange, true)
  },
  methods: {
    hasChildren(opt) {
      return opt[this.childrenKey] && opt[this.childrenKey].length
    },

    togglePanel() {
      if (this.disabled) return
      this.visible = !this.visible
      if (this.visible) {
        this.$nextTick(() => {
          this.updatePlacement()
          this.syncExpandingPath()
        })
      }
    },

    closePanel() {
      this.visible = false
      this.panelStyle = {}
    },

    onDocumentClick(e) {
      if (!this.visible) return
      const trigger = this.$refs.trigger
      const panel = this.$refs.panel
      if (
        (trigger && trigger.contains(e.target)) ||
        (panel && panel.contains(e.target))
      ) return
      this.closePanel()
    },

    onWindowChange() {
      if (this.visible) this.updatePlacement()
    },

    updatePlacement() {
      const trigger = this.$refs.trigger
      const panel = this.$refs.panel
      if (!trigger) return

      const rect = trigger.getBoundingClientRect()
      const gap = 4
      const estPanelHeight = 260

      // 垂直方向：优先锚定在 trigger 下方，空间不够则翻到上方
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      let anchorBelow = spaceBelow >= estPanelHeight || spaceBelow >= spaceAbove

      const style = {
        position: 'fixed',
        left: `${rect.left}px`,
        minWidth: `${rect.width}px`
      }

      if (anchorBelow) {
        style.top = `${rect.bottom + gap}px`
      } else {
        style.top = `${rect.top - estPanelHeight - gap}px`
      }

      this.panelStyle = style

      // 渲染后根据实际尺寸校准位置
      if (panel) {
        this.$nextTick(() => {
          const panelRect = panel.getBoundingClientRect()
          const actualHeight = panelRect.height
          const actualWidth = panelRect.width

          if (actualHeight === 0) return

          const adjusted = { ...this.panelStyle }

          // 垂直：以实际高度重新锚定
          if (anchorBelow && rect.bottom + gap + actualHeight > window.innerHeight - gap) {
            // 下方放不下，尝试翻到上方
            if (rect.top - actualHeight - gap >= gap) {
              adjusted.top = `${rect.top - actualHeight - gap}px`
            } else {
              // 上下都不够，保持下方但限制高度
              adjusted.top = `${rect.bottom + gap}px`
              adjusted.maxHeight = `${window.innerHeight - rect.bottom - gap * 2}px`
              adjusted.overflow = 'auto'
            }
          } else if (!anchorBelow && rect.top - actualHeight - gap < gap) {
            // 上方放不下，尝试翻到下方
            if (rect.bottom + gap + actualHeight <= window.innerHeight - gap) {
              adjusted.top = `${rect.bottom + gap}px`
            } else {
              // 上下都不够，保持上方但限制高度
              adjusted.top = `${gap}px`
              adjusted.maxHeight = `${rect.top - gap * 2}px`
              adjusted.overflow = 'auto'
            }
          } else if (!anchorBelow) {
            // 上方足够，用实际高度修正
            adjusted.top = `${rect.top - actualHeight - gap}px`
          }

          // 水平：根据实际宽度校准
          if (actualWidth > 0 && rect.left + actualWidth > window.innerWidth - gap) {
            adjusted.left = `${Math.max(gap, window.innerWidth - actualWidth - gap)}px`
          }

          this.panelStyle = adjusted
        })
      }
    },

    syncExpandingPath() {
      const val = this.value
      if (!val || !val.length) {
        this.expandingPath = []
        return
      }
      const path = []
      let nodes = this.options
      for (const v of val) {
        const node = nodes.find(n => n[this.valueKey] === v)
        if (!node) break
        const children = node[this.childrenKey]
        if (children && children.length) {
          path.push(node)
          nodes = children
        } else {
          break
        }
      }
      this.expandingPath = path
    },

    getLabelPath(path) {
      if (!path || !path.length) return ''
      const labels = []
      let nodes = this.options
      for (const v of path) {
        const node = nodes.find(n => n[this.valueKey] === v)
        if (!node) break
        labels.push(node[this.labelKey])
        nodes = node[this.childrenKey] || []
      }
      return labels.join(' / ')
    },

    isChecked(opt, colIdx) {
      const val = this.value
      if (!val || !val.length || val.length <= colIdx) return false
      return val[colIdx] === opt[this.valueKey]
    },

    nodeClass(opt, colIdx) {
      const isActive = this.isChecked(opt, colIdx)

      let inExpandingPath = false
      if (!isActive && colIdx < this.expandingPath.length) {
        inExpandingPath = this.expandingPath[colIdx][this.valueKey] === opt[this.valueKey]
      }

      return {
        'is-active': isActive,
        'in-active-path': inExpandingPath
      }
    },

    handleNodeClick(opt, colIdx) {
      const hasKids = this.hasChildren(opt)

      // 更新展开路径：截断到当前列，若有子级则展开
      this.expandingPath = this.expandingPath.slice(0, colIdx)
      if (hasKids) {
        this.expandingPath.push(opt)
      }

      // 构建新路径：保留当前列之前已选的值 + 当前选中值
      const prevValues = (this.value || []).slice(0, colIdx)
      const newPath = [...prevValues, opt[this.valueKey]]

      this.selectPath(newPath)

      if (!hasKids) {
        this.closePanel()
      }
    },

    selectPath(path) {
      this.targetKey && insertStep(this, [this.targetKey], path)
      this.$emit('input', path)
      this.$emit('change', path)
    },

    clearValue() {
      const val = []
      this.targetKey && insertStep(this, [this.targetKey], val)
      this.$emit('input', val)
      this.$emit('change', val)
    }
  }
}
</script>

<style lang="less">
.im-cascader {
  position: relative;
  width: 100%;
  line-height: 36px;
  font-size: 13px;
}

.im-cascader__trigger {
  display: flex;
  align-items: center;
  min-height: 36px;
  padding: 0 30px 0 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: white;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.2);
  }

  .is-opened & {
    border-color: var(--primary-color);
    background: rgba(255, 255, 255, 0.1);
    box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.2);
  }

  .is-disabled & {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.im-cascader__label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: white;
}

.im-cascader__placeholder {
  flex: 1;
  color: rgba(255, 255, 255, 0.3);
}

.im-cascader__suffix {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
}

.im-cascader__clear {
  font-style: normal;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  line-height: 1;
  position: absolute;
  right: -4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;

  .im-cascader:hover & {
    opacity: 1;
  }

  &:hover {
    color: white;
  }
}

.im-cascader__arrow {
  display: inline-block;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid rgba(255, 255, 255, 0.4);
  transition: transform 0.3s, opacity 0.2s;

  &.is-reverse {
    transform: rotate(180deg);
  }

  .im-cascader:hover &.has-clear {
    opacity: 0;
  }
}

.im-cascader__panel {
  z-index: 5000;
  display: flex;
  background: rgba(30, 30, 35, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  overflow: hidden;
  padding: 4px;
}

.im-cascader__menu {
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  min-width: 160px;
  flex: 1 1 0%;

  &:last-child {
    border-right: none;
  }
}

.im-cascader__menu-wrap {
  max-height: 244px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 2px;
  }
}

.im-cascader__node {
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.75);
  font-size: 13px;
  height: 34px;
  line-height: 34px;
  padding: 0 14px;
  border-radius: 8px;
  margin: 1px 0;
  transition: all 0.2s;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: white;
  }

  &.in-active-path {
    background: rgba(255, 255, 255, 0.05);
    color: white;
    font-weight: 500;
  }

  &.is-active {
    background: linear-gradient(90deg, rgba(var(--primary-color-rgb), 0.2), rgba(var(--primary-color-rgb), 0.08));
    color: var(--primary-color);
    font-weight: 600;
  }
}

.im-cascader__node-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.im-cascader__node-arrow {
  font-style: normal;
  color: rgba(255, 255, 255, 0.3);
  font-size: 14px;
  margin-left: 8px;
  transition: color 0.2s;
  flex-shrink: 0;

  .im-cascader__node:hover & {
    color: rgba(255, 255, 255, 0.6);
  }
}

.im-cascader__radio {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.3);
  margin-right: 8px;
  flex-shrink: 0;
  transition: all 0.2s;

  &.is-checked {
    border-color: var(--primary-color);
  }
}

.im-cascader__radio-inner {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: transparent;
  transition: background 0.2s;

  .im-cascader__radio.is-checked & {
    background: var(--primary-color);
  }
}

.im-cascader__empty {
  color: rgba(255, 255, 255, 0.3);
  text-align: center;
  padding: 12px 0;
}

.im-cascader-fade-enter-active,
.im-cascader-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.im-cascader-fade-enter,
.im-cascader-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
